import { useLocalParticipant, useMediaDeviceSelect, useTrackVolume } from '@livekit/components-react';
import { useEffect, useState, type ReactElement } from 'react';

/**
 * Input and output device pickers, plus a live level meter.
 *
 * The meter is the point of this panel, not a decoration. A microphone that
 * captures silence — a virtual device left behind by a remote-desktop tool, a
 * headset the system switched away from — is indistinguishable from a broken
 * call: the track publishes, the server forwards it, and the other side hears
 * nothing. Watching your own level answers that in two seconds, alone, without
 * needing a second person on the call to tell you.
 */

const STORAGE_PREFIX = 'discordo:audio-device:';

/**
 * Reads a remembered device.
 *
 * Wrapped because storage access itself throws in a private window, not just
 * returns empty — an unguarded read takes the whole panel down with it.
 */
function readStored(kind: MediaDeviceKind): string | null {
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + kind);
  } catch {
    return null;
  }
}

function writeStored(kind: MediaDeviceKind, deviceId: string): void {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + kind, deviceId);
  } catch {
    // A remembered device is a convenience; losing it must not break the call.
  }
}

interface DevicePickerProps {
  kind: MediaDeviceKind;
  label: string;
}

function DevicePicker({ kind, label }: DevicePickerProps): ReactElement {
  const { devices, activeDeviceId, setActiveMediaDevice } = useMediaDeviceSelect({ kind });
  const [restored, setRestored] = useState(false);

  // Restores the remembered choice once the browser has named the devices.
  // Before permission is granted the labels come back empty and the ids are
  // unstable, so restoring earlier would pin the wrong device.
  useEffect(() => {
    if (restored || devices.length === 0) {
      return;
    }

    const remembered = readStored(kind);
    if (remembered !== null && devices.some((device) => device.deviceId === remembered)) {
      void setActiveMediaDevice(remembered);
    }
    setRestored(true);
  }, [devices, kind, restored, setActiveMediaDevice]);

  if (devices.length === 0) {
    return (
      <label className="audio-setting">
        <span className="audio-setting-label">{label}</span>
        <span className="audio-setting-empty">Nenhum dispositivo encontrado</span>
      </label>
    );
  }

  return (
    <label className="audio-setting">
      <span className="audio-setting-label">{label}</span>
      <select
        className="audio-setting-select"
        value={activeDeviceId ?? ''}
        onChange={(event) => {
          const deviceId = event.target.value;
          writeStored(kind, deviceId);
          void setActiveMediaDevice(deviceId);
        }}
      >
        {devices.map((device, index) => (
          <option key={device.deviceId} value={device.deviceId}>
            {device.label || `${label} ${index + 1}`}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Turns the 0..1 level LiveKit reports into a percentage worth looking at. */
export function meterWidth(volume: number): number {
  // Speech sits low on a linear scale, so a raw reading barely moves the bar.
  // The multiplier is what makes normal talking fill a visible share of it.
  const scaled = Math.sqrt(Math.max(volume, 0)) * 140;
  return Math.min(Math.round(scaled), 100);
}

function InputMeter(): ReactElement {
  const { microphoneTrack } = useLocalParticipant();
  // `microphoneTrack` is the publication; the meter needs the track it carries.
  const volume = useTrackVolume(microphoneTrack?.audioTrack);
  const width = meterWidth(volume ?? 0);

  return (
    <div className="audio-meter" aria-label="Nível do microfone">
      <div className="audio-meter-fill" style={{ width: `${width}%` }} />
      <span className="audio-meter-hint">
        {microphoneTrack?.audioTrack === undefined
          ? 'Microfone desligado'
          : width < 3
            ? 'Fale para testar — se a barra não se mexer, o dispositivo está mudo'
            : 'Captando'}
      </span>
    </div>
  );
}

export function AudioSettings(): ReactElement {
  const [open, setOpen] = useState(false);

  return (
    <div className="audio-settings">
      <button
        type="button"
        className="audio-settings-toggle"
        aria-expanded={open}
        onClick={() => {
          setOpen((previous) => !previous);
        }}
      >
        ⚙ Áudio
      </button>

      {open && (
        <div className="audio-settings-panel">
          <DevicePicker kind="audioinput" label="Microfone" />
          <DevicePicker kind="audiooutput" label="Saída de som" />
          <InputMeter />
        </div>
      )}
    </div>
  );
}

export default AudioSettings;
